import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import { ArrowLeft, ShieldStar, Printer, DownloadSimple, HandHeart } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DeepDiveReport() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/career-deepdive/reports/${id}`);
        setReport(data);
      } catch (e) {
        if (e.response?.status === 401) navigate("/");
        else setError(e.response?.data?.detail || "Could not load report");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, navigate]);

  const download = () => {
    if (!report) return;
    const blob = new Blob([report.content_markdown], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `career-deep-dive-${report.branch}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return <div className="max-w-3xl mx-auto px-6 py-24 text-slate-600">Loading your report…</div>;

  if (error || !report) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24">
        <p className="text-red-600">{error || "Report not found."}</p>
        <Link to="/dashboard" className="mt-4 inline-flex items-center gap-2 text-[#0033A0] font-semibold">
          <ArrowLeft size={16} weight="bold" /> Back to dashboard
        </Link>
      </div>
    );
  }

  return (
    <main className="max-w-4xl mx-auto px-6 md:px-10 py-12 md:py-16" data-testid="deepdive-report-page">
      <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-[#0033A0]">
        <ArrowLeft size={16} weight="bold" /> Dashboard
      </Link>

      <div className="mt-6 flex items-start justify-between gap-6 flex-wrap">
        <div>
          <span className="inline-flex items-center gap-2 bg-[#0033A0]/10 text-[#0033A0] px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider">
            <ShieldStar size={14} weight="fill" /> Premium report
          </span>
          <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter text-slate-900 mt-4 leading-[1.05]">
            {report.branch_display} Career Deep Dive
          </h1>
          <p className="text-slate-600 mt-3 text-sm">
            {report.jobs.length} job{report.jobs.length > 1 ? "s" : ""}: {report.jobs.join(" • ")}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => window.print()} data-testid="report-print-btn" className="inline-flex items-center gap-2 bg-white border border-slate-300 hover:bg-slate-100 text-slate-900 font-semibold py-2.5 px-4 rounded-md text-sm">
            <Printer size={16} weight="bold" /> Print
          </button>
          <button onClick={download} data-testid="report-download-btn" className="inline-flex items-center gap-2 bg-white border border-slate-300 hover:bg-slate-100 text-slate-900 font-semibold py-2.5 px-4 rounded-md text-sm">
            <DownloadSimple size={16} weight="bold" /> Markdown
          </button>
        </div>
      </div>

      <div className="mt-10 bg-white border border-slate-200 rounded-2xl p-6 md:p-10 prose prose-slate max-w-none prose-headings:font-display prose-h2:text-2xl prose-h3:text-lg prose-h2:mt-10 prose-h2:mb-4 prose-h3:mt-6 prose-h3:mb-2 prose-h2:border-b prose-h2:border-slate-200 prose-h2:pb-3 prose-li:my-0.5">
        <ReactMarkdown>{report.content_markdown}</ReactMarkdown>
      </div>

      <div className="mt-10 bg-slate-50 border border-slate-200 rounded-xl p-6 flex items-start gap-4">
        <HandHeart size={28} weight="duotone" className="text-[#D90429] shrink-0" />
        <div>
          <h4 className="font-display font-bold text-slate-900">Thanks for supporting veterans.</h4>
          <p className="text-slate-700 text-sm mt-1 leading-relaxed">
            50% of the net profit from this purchase is committed to veteran-support organizations. Net profit = revenue minus Stripe fees, AI generation costs, and direct hosting.
          </p>
        </div>
      </div>

      <p className="text-xs text-slate-500 mt-8 leading-relaxed">
        This report is AI-generated using publicly known career field information. Specific bonuses, eligibility rules, and contract terms change frequently — always confirm details with an official recruiter and authoritative sources (your branch{`'`}s official site, MyArmyBenefits, Navy COOL, AF COOL, etc.) before making decisions.
      </p>
    </main>
  );
}
